import React, { useState } from 'react';
import { CheckCircle, XCircle, ChevronDown, ChevronUp, AlertTriangle, Clock, Wrench, Calendar, HardDrive, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';

const precos = [
  {
    titulo: 'Formatação Simples',
    subtitulo: 'Reinstalação do Windows',
    faixa: 'R$ 80 – R$ 120',
    inclui: ['Instalação do Windows 10 ou 11', 'Drivers básicos do hardware', 'Atualizações do sistema'],
    destaque: false,
    icon: <HardDrive className="w-5 h-5" />,
  },
  {
    titulo: 'Formatação Completa',
    subtitulo: 'Windows + programas + backup',
    faixa: 'R$ 150 – R$ 220',
    inclui: ['Tudo da Formatação Simples', 'Backup dos seus arquivos', 'Programas essenciais (navegador, PDF, antivírus)', 'Limpeza interna do equipamento'],
    destaque: true,
    icon: <Zap className="w-5 h-5" />,
  },
  {
    titulo: 'Formatação + Office',
    subtitulo: 'Com pacote Microsoft',
    faixa: 'R$ 200 – R$ 280',
    inclui: ['Tudo da Formatação Completa', 'Instalação do Microsoft Office', 'Ativação inclusa'],
    destaque: false,
    icon: <Wrench className="w-5 h-5" />,
  },
  {
    titulo: 'Formatação + SSD',
    subtitulo: 'Upgrade de desempenho',
    faixa: 'R$ 320 – R$ 480',
    inclui: ['Tudo da Formatação Completa', 'Troca do HD por SSD', 'PC até 3× mais rápido', 'SSD de 240GB ou 480GB (à escolha)'],
    destaque: false,
    icon: <HardDrive className="w-5 h-5" />,
  },
];

const sintomasFormatar = [
  { ok: true,  texto: 'PC demora mais de 5 minutos para iniciar' },
  { ok: true,  texto: 'Travamentos e lentidão frequentes sem causa aparente' },
  { ok: true,  texto: 'Vírus ou malware que antivírus não remove' },
  { ok: true,  texto: 'Erros constantes do Windows (tela azul, crash de programas)' },
  { ok: true,  texto: 'HD com mais de 3 anos e nunca foi formatado' },
  { ok: true,  texto: 'Mudança de usuário ou revenda do equipamento' },
];

const naoValeFormatar = [
  { ok: false, texto: 'Problema é físico: HD com barulho, superaquecimento' },
  { ok: false, texto: 'RAM danificada ou em quantidade insuficiente (< 4GB)' },
  { ok: false, texto: 'Equipamento com mais de 10 anos sem upgrade de hardware' },
];

const faqs = [
  {
    pergunta: 'Quanto custa formatar um computador em Ribeirão Preto?',
    resposta:
      'Em Ribeirão Preto, a formatação básica custa entre R$ 80 e R$ 120. A formatação completa — com backup, drivers, programas e limpeza — fica entre R$ 150 e R$ 220. Na Fernando Tech, o diagnóstico é gratuito antes de qualquer serviço.',
  },
  {
    pergunta: 'Quanto tempo leva para formatar um computador?',
    resposta:
      'Uma formatação simples leva de 2 a 4 horas. Com backup e instalação de programas, pode levar de 4 a 8 horas. Equipamentos com HD antigo (lento) tendem a demorar mais. Na Fernando Tech entregamos em até 24h na maioria dos casos.',
  },
  {
    pergunta: 'Preciso levar meu computador até vocês?',
    resposta:
      'Sim. Para formatação, é necessário levar o equipamento até nossa assistência técnica em Ribeirão Preto. Em casos específicos, atendemos empresas com contrato de suporte diretamente no local.',
  },
  {
    pergunta: 'Meus arquivos são apagados na formatação?',
    resposta:
      'Sim — a formatação apaga tudo. Por isso, o serviço de backup é altamente recomendado. Fazemos backup em HD externo ou nuvem antes de formatar. Se o HD já falhou, oferecemos também serviço de recuperação de dados.',
  },
  {
    pergunta: 'Vale a pena formatar um computador lento?',
    resposta:
      'Depende. Se a lentidão é causada por vírus, muito lixo acumulado ou sistema corrompido, a formatação resolve. Se o problema for hardware (pouca RAM, HD antigo), recomendamos upgrade. Nossa análise gratuita identifica a causa antes de qualquer decisão.',
  },
  {
    pergunta: 'Qual a diferença entre formatação e factory reset?',
    resposta:
      'O factory reset restaura o PC às configurações de fábrica, mas pode manter arquivos de sistema e vírus. A formatação profissional apaga tudo e instala o Windows limpo, sem resíduos — resultado muito mais seguro e rápido.',
  },
];

function FaqItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-700/60 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left bg-slate-800/60 hover:bg-slate-700/60 transition-colors"
        aria-expanded={open}
        data-testid={`faq-item-${item.pergunta.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
      >
        <span className="text-slate-100 font-medium text-sm leading-snug pr-4">{item.pergunta}</span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-cyan-400 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 py-4 bg-slate-900/60 text-slate-300 text-sm leading-relaxed border-t border-slate-700/40">
          {item.resposta}
        </div>
      )}
    </div>
  );
}

export default function ArtigoFormatacaoPC({ onAgendar }) {
  return (
    <article
      className="max-w-4xl mx-auto px-4 py-12"
      itemScope
      itemType="https://schema.org/Article"
    >
      {/* ── Manchete ────────────────────────────────────────────────────── */}
      <header className="mb-10">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs font-semibold uppercase tracking-widest text-cyan-400 bg-cyan-400/10 px-3 py-1 rounded-full">
            Guia 2026
          </span>
          <span className="text-xs text-slate-500 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Leitura: ~4 min
          </span>
        </div>
        <h1
          className="text-3xl sm:text-4xl font-extrabold text-white leading-tight mb-4"
          itemProp="headline"
        >
          Quanto Custa Formatar um Computador em 2026?
        </h1>
        <p className="text-slate-400 text-sm">
          Por <span className="text-cyan-400 font-medium">Fernando Tech</span> · Ribeirão Preto, SP ·
          Atualizado em janeiro de 2026
        </p>
      </header>

      {/* ── Resposta Direta ─────────────────────────────────────────────── */}
      <section
        className="bg-gradient-to-r from-cyan-950/60 to-slate-800/60 border border-cyan-700/40 rounded-2xl p-6 mb-10"
        aria-label="Resposta rápida"
      >
        <p className="text-xs font-bold uppercase tracking-widest text-cyan-400 mb-2">
          Resposta rápida
        </p>
        <p className="text-white text-lg font-semibold leading-relaxed">
          Em 2026, formatar um computador custa entre{' '}
          <span className="text-cyan-300 font-bold">R$ 80 e R$ 480</span>, dependendo do tipo de
          serviço. A formatação simples (só Windows) custa a partir de{' '}
          <span className="text-cyan-300 font-bold">R$ 80</span>. Com backup, programas e limpeza,
          a média fica em{' '}
          <span className="text-cyan-300 font-bold">R$ 150 a R$ 220</span>. Em Ribeirão Preto, a{' '}
          <strong>Fernando Tech</strong> oferece diagnóstico gratuito antes de qualquer serviço.
        </p>
      </section>

      {/* ── O que é formatação ─────────────────────────────────────────── */}
      <section className="mb-10">
        <h2 className="text-xl font-bold text-white mb-3">O que é a formatação de computador?</h2>
        <p className="text-slate-400 leading-relaxed mb-4">
          Formatar um computador significa apagar tudo que está no HD ou SSD e reinstalar o sistema
          operacional do zero — como se fosse um PC novo. O processo elimina vírus, lentidão por
          acúmulo de arquivos, configurações corrompidas e a maioria dos erros de sistema.
        </p>
        <p className="text-slate-400 leading-relaxed">
          É diferente de um simples "restaurar para configurações de fábrica": a formatação
          profissional instala uma versão limpa do Windows, sem resíduos de fábrica ou arquivos
          temporários antigos.
        </p>
      </section>

      {/* ── Tabela de Preços ────────────────────────────────────────────── */}
      <section className="mb-12">
        <h2 className="text-xl font-bold text-white mb-6">
          Tabela de preços de formatação em 2026
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {precos.map((p) => (
            <div
              key={p.titulo}
              className={`relative rounded-2xl border p-5 transition-all ${
                p.destaque
                  ? 'border-cyan-500/60 bg-gradient-to-br from-cyan-950/80 to-slate-800/80 shadow-lg shadow-cyan-500/10'
                  : 'border-slate-700/50 bg-slate-800/50 hover:border-slate-600/60'
              }`}
            >
              {p.destaque && (
                <span className="absolute -top-3 left-4 bg-cyan-500 text-slate-950 text-[11px] font-bold px-3 py-0.5 rounded-full uppercase tracking-wide">
                  Mais solicitado
                </span>
              )}
              <div className="flex items-center gap-2 mb-1">
                <span className={p.destaque ? 'text-cyan-400' : 'text-slate-400'}>{p.icon}</span>
                <h3 className="text-white font-bold text-base">{p.titulo}</h3>
              </div>
              <p className="text-slate-500 text-xs mb-3">{p.subtitulo}</p>
              <p className={`text-2xl font-extrabold mb-4 ${p.destaque ? 'text-cyan-300' : 'text-slate-200'}`}>
                {p.faixa}
              </p>
              <ul className="space-y-1.5">
                {p.inclui.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-slate-400">
                    <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="text-slate-500 text-xs mt-4 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-500" />
          Valores de referência para Ribeirão Preto e região. Preço final após avaliação gratuita.
        </p>
      </section>

      {/* ── Quando vale a pena formatar ─────────────────────────────────── */}
      <section className="mb-10">
        <h2 className="text-xl font-bold text-white mb-5">Quando vale a pena formatar?</h2>
        <p className="text-slate-400 mb-5 leading-relaxed">
          A formatação é a solução certa quando o problema é de software. Veja os principais sinais
          de que chegou a hora:
        </p>

        <div className="grid sm:grid-cols-2 gap-6 mb-6">
          {/* Pode formatar */}
          <div>
            <p className="text-emerald-400 font-semibold text-sm mb-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Formatação resolve
            </p>
            <ul className="space-y-2">
              {sintomasFormatar.map((s) => (
                <li key={s.texto} className="flex items-start gap-2 text-sm text-slate-400">
                  <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                  {s.texto}
                </li>
              ))}
            </ul>
          </div>

          {/* Não resolve */}
          <div>
            <p className="text-rose-400 font-semibold text-sm mb-3 flex items-center gap-2">
              <XCircle className="w-4 h-4" /> Formatação não resolve
            </p>
            <ul className="space-y-2">
              {naoValeFormatar.map((s) => (
                <li key={s.texto} className="flex items-start gap-2 text-sm text-slate-400">
                  <XCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                  {s.texto}
                </li>
              ))}
            </ul>
            <p className="text-slate-500 text-xs mt-4 leading-relaxed">
              Nesses casos, recomendamos uma avaliação técnica para decidir entre reparo de hardware
              ou upgrade.
            </p>
          </div>
        </div>
      </section>

      {/* ── Formatar ou trocar ──────────────────────────────────────────── */}
      <section className="mb-12">
        <h2 className="text-xl font-bold text-white mb-4">Formatar ou trocar o computador?</h2>
        <p className="text-slate-400 leading-relaxed mb-4">
          Se o seu PC tem menos de 5 anos, formatar quase sempre compensa. Com SSD, o equipamento
          ganha nova vida e fica mais rápido do que computadores novos de entrada. Use esta regra
          prática:
        </p>
        <div className="bg-slate-800/60 border border-slate-700/40 rounded-xl p-5">
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3 text-slate-300">
              <span className="bg-cyan-500/20 text-cyan-300 font-bold rounded px-2 py-0.5 text-xs shrink-0 mt-0.5">PC &lt; 5 anos</span>
              Formate + faça upgrade para SSD. Custo entre R$ 320 e R$ 480, resultado equivalente a um PC novo de entrada.
            </li>
            <li className="flex items-start gap-3 text-slate-300">
              <span className="bg-amber-500/20 text-amber-300 font-bold rounded px-2 py-0.5 text-xs shrink-0 mt-0.5">PC 5–8 anos</span>
              Avalie antes. Formate apenas se o hardware ainda atende às suas necessidades.
            </li>
            <li className="flex items-start gap-3 text-slate-300">
              <span className="bg-rose-500/20 text-rose-300 font-bold rounded px-2 py-0.5 text-xs shrink-0 mt-0.5">PC &gt; 8 anos</span>
              Na maioria dos casos, trocar é mais econômico a longo prazo. Mas a avaliação vai confirmar.
            </li>
          </ul>
        </div>
      </section>

      {/* ── CTA Local ───────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-slate-800/80 to-cyan-950/60 border border-cyan-700/30 rounded-2xl p-7 mb-12 text-center">
        <h2 className="text-xl font-bold text-white mb-2">
          Formatação de Computador em Ribeirão Preto
        </h2>
        <p className="text-slate-400 text-sm mb-6 max-w-lg mx-auto leading-relaxed">
          A <strong className="text-white">Fernando Tech</strong> realiza formatações profissionais
          em notebooks e desktops em Ribeirão Preto desde 2008. Diagnóstico gratuito, entrega em até
          24h, garantia de 90 dias no serviço.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={onAgendar}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-700 hover:to-cyan-700 text-white font-bold rounded-full shadow-lg shadow-cyan-500/20 transition-all text-sm"
            data-testid="artigo-agendar-btn"
          >
            <Calendar className="w-4 h-4" />
            Agendar Diagnóstico Gratuito
          </button>
          <a
            href="https://wa.me/5516991378312?text=Olá!%20Preciso%20de%20formatação%20de%20computador."
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-full shadow-lg shadow-emerald-500/20 transition-all text-sm"
            data-testid="artigo-whatsapp-btn"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
              <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.533 5.85L0 24l6.338-1.497A11.93 11.93 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.789 9.789 0 01-4.99-1.364l-.358-.213-3.76.888.937-3.647-.234-.374A9.786 9.786 0 012.182 12C2.182 6.57 6.57 2.182 12 2.182c5.43 0 9.818 4.388 9.818 9.818 0 5.43-4.388 9.818-9.818 9.818z"/>
            </svg>
            Pedir Orçamento pelo WhatsApp
          </a>
        </div>
        <p className="text-slate-500 text-xs mt-4">
          Atendemos Ribeirão Preto, Sertãozinho, Jardinópolis e região.
        </p>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────── */}
      <section aria-label="Perguntas frequentes sobre formatação">
        <h2 className="text-xl font-bold text-white mb-5">
          Perguntas frequentes sobre formatação de computador
        </h2>
        <div className="space-y-2">
          {faqs.map((item) => (
            <FaqItem key={item.pergunta} item={item} />
          ))}
        </div>
      </section>

      {/* ── Links internos ──────────────────────────────────────────────── */}
      <section className="mt-10 pt-8 border-t border-slate-800">
        <p className="text-slate-500 text-xs uppercase tracking-widest mb-4">Veja também</p>
        <div className="flex flex-wrap gap-3">
          {[
            { label: 'Assistência Técnica em Notebook', href: '/assistencia-tecnica' },
            { label: 'Assistência Daten Autorizada', href: '/daten' },
            { label: 'Orçamento Câmeras Intelbras', href: '/orcamento-intelbras' },
          ].map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="text-sm text-cyan-400 hover:text-cyan-300 border border-cyan-800/50 hover:border-cyan-600/60 px-4 py-1.5 rounded-full transition-all"
            >
              {link.label}
            </Link>
          ))}
        </div>
      </section>
    </article>
  );
}
